import React, { useEffect, useState } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { View, StatusBar, StyleSheet, Platform, Alert, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import MainNUST from './src/screens/home/MainNUST';
import CampusMapScreen from './src/screens/CampusMapScreen';
import TimetableScreen from './src/screens/TimetableScreen';
import ResourcesScreen from './src/screens/ResourcesScreen';
import WebViewScreen from './src/screens/WebViewScreen';
import AnnouncementsScreen from './src/screens/AnnouncementsScreen';
import AnimatedSplashScreen from './src/components/AnimatedSplashScreen';
import { colors } from './src/constants/colors';
import { setupNotificationHandlers, rescheduleAllNotifications } from './src/services/notifications';
import { firebaseService } from './src/services/FirebaseService';
import LoginScreen from './src/screens/auth/LoginScreen';
import SignUpScreen from './src/screens/auth/SignUpScreen';
import VerificationScreen from './src/screens/auth/VerificationScreen';
import CourseSelectionScreen from './src/screens/CourseSelectionScreen';
import HowToScreen from './src/screens/HowToScreen';
import { registerForPushNotificationsAsync, storePushToken, startAnnouncementListener, setupAnnouncementNotificationListeners } from './src/services/announcementNotifications';
import { userPresenceService } from './src/services/UserPresenceService';
import { databaseInitService } from './src/services/DatabaseInitService';

const Stack = createNativeStackNavigator();
const Tab = createBottomTabNavigator();

function TabNavigator() {
  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;
          if (route.name === 'Home') {
            iconName = focused ? 'home-sharp' : 'home-outline';
          } else if (route.name === 'Timetable') {
            iconName = focused ? 'time-sharp' : 'time-outline';
          } else if (route.name === 'Announcements') {
            iconName = focused ? 'megaphone' : 'megaphone-outline';
          } else if (route.name === 'Map') {
            iconName = focused ? 'location-sharp' : 'location-outline';
          } else if (route.name === 'Resources') {
            iconName = focused ? 'book-sharp' : 'book-outline';
          }
          return <Ionicons name={iconName as any} size={size} color={color} />;
        },
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.textSecondary,
        tabBarStyle: styles.tabBar,
      })}
    >
      <Tab.Screen name="Home" component={MainNUST} options={{ headerShown: false }} />
      <Tab.Screen name="Timetable" component={TimetableScreen} options={{ headerShown: false }} />
      <Tab.Screen name="Announcements" component={AnnouncementsScreen} options={{ headerShown: false }} />
      <Tab.Screen name="Map" component={CampusMapScreen} options={{ headerShown: false }} />
      <Tab.Screen name="Resources" component={ResourcesScreen} options={{ headerShown: false }} />
    </Tab.Navigator>
  );
}

export default function App() {
  const [isFirstLaunch, setIsFirstLaunch] = useState<boolean | null>(null);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hasSeenHowTo, setHasSeenHowTo] = useState(false);
  const [initialRoute, setInitialRoute] = useState('Main');

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = firebaseService.subscribeToAuthChanges((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    async function prepare() {
      try {
        // Check if user has seen How2Screen
        const hasSeenHowTo = await AsyncStorage.getItem('hasSeenHowTo');
        setHasSeenHowTo(!!hasSeenHowTo);

        // Setup notifications
        await setupNotificationHandlers();
        
        // Request notification permissions
        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        let finalStatus = existingStatus;
        if (existingStatus !== 'granted') {
          const { status } = await Notifications.requestPermissionsAsync();
          finalStatus = status;
        }

        setPermissionsGranted(finalStatus === 'granted');

        // Check if user has selected a course
        const selectedCourse = await AsyncStorage.getItem('@selected_course');
        if (!selectedCourse) {
          setInitialRoute('CourseSelection');
        }

        // Reschedule notifications on app start
        if (finalStatus === 'granted') {
          await rescheduleAllNotifications();
        }
      } catch (e) {
        console.warn(e);
      } finally {
        setIsReady(true);
      }
    }

    prepare();
  }, []);

  useEffect(() => {
    let unsubscribeAnnouncements: (() => void) | undefined;
    let cleanupNotificationListeners: (() => void) | undefined;

    async function setupUserServices() {
      if (user && permissionsGranted) {
        try {
          // Register for push notifications and store token
          const token = await registerForPushNotificationsAsync();
          if (token) {
            await storePushToken(token);
          }

          // Initialize database and presence tracking
          await databaseInitService.initializePresenceSystem();
          await userPresenceService.setupPresence();

          // Start announcement listener only when user is logged in
          unsubscribeAnnouncements = startAnnouncementListener();
          
          // Setup announcement notification listeners
          cleanupNotificationListeners = setupAnnouncementNotificationListeners();
        } catch (error) {
          console.error('Error setting up user services:', error);
        }
      }
    }

    setupUserServices();

    // Cleanup function
    return () => {
      if (unsubscribeAnnouncements) {
        unsubscribeAnnouncements();
      }
      if (cleanupNotificationListeners) {
        cleanupNotificationListeners();
      }
      // Cleanup presence service
      userPresenceService.cleanup();
    };
  }, [user, permissionsGranted]);

  const handleSplashComplete = () => {
    setIsReady(true);
  };

  const requestPermissions = async () => {
    try {
      // Request notifications permission
      const notificationPermission = await Notifications.requestPermissionsAsync({
        ios: {
          allowAlert: true,
          allowBadge: true,
          allowSound: true,
          allowAnnouncements: true,
        },
      });

      // Request storage permissions
      const { status } = await Notifications.requestPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permissions Required',
          'This app needs notification and storage permissions to function properly. Please enable them in your device settings.',
          [
            {
              text: 'Open Settings',
              onPress: () => {
                Platform.OS === 'ios'
                  ? Linking.openURL('app-settings:')
                  : Linking.openSettings();
              },
            },
            { text: 'Continue Anyway', onPress: () => setPermissionsGranted(true) },
          ]
        );
      } else {
        setPermissionsGranted(true);
      }
    } catch (error) {
      console.error('Error requesting permissions:', error);
      Alert.alert(
        'Error',
        'Failed to request permissions. Some features may not work properly.',
        [{ text: 'OK', onPress: () => setPermissionsGranted(true) }]
      );
    }
  };

  const checkIfFirstLaunch = async () => {
    try {
      const hasLaunched = await AsyncStorage.getItem('hasLaunched');
      setIsFirstLaunch(hasLaunched === null);
    } catch (error) {
      console.error('Error checking first launch:', error);
      setIsFirstLaunch(false);
    }
  };

  if (loading) {
    return <AnimatedSplashScreen onComplete={handleSplashComplete} />;
  }

  return (
    <SafeAreaProvider>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <NavigationContainer>
          <Stack.Navigator screenOptions={{ headerShown: false }}>
            {user ? (
              <>
                {!hasSeenHowTo ? (
                  <Stack.Screen 
                    name="HowTo" 
                    component={HowToScreen}
                    initialParams={{ setHasSeenHowTo }}
                  />
                ) : (
                  <>
                    <Stack.Screen name="Main" component={TabNavigator} />
                    <Stack.Screen name="WebView" component={WebViewScreen} />
                    <Stack.Screen name="CourseSelection" component={CourseSelectionScreen} />
                  </>
                )}
              </>
            ) : (
              <>
                <Stack.Screen 
                  name="Login" 
                  component={LoginScreen}
                  options={{ headerShown: false }}
                />
                <Stack.Screen 
                  name="SignUp" 
                  component={SignUpScreen}
                  options={{ headerShown: false }}
                />
                <Stack.Screen 
                  name="Verification" 
                  component={VerificationScreen}
                  options={{ headerShown: false }}
                />
              </>
            )}
          </Stack.Navigator>
        </NavigationContainer>
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  tabBar: {
    backgroundColor: colors.surface,
    borderTopWidth: 0,
    height: 60,
    paddingBottom: 8,
    paddingTop: 8,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  iconContainer: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 18,
  },
  activeIconContainer: {
    backgroundColor: `${colors.primary}15`,
  },
  tabLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 2,
  },
});
